import React, { useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaUsers,
  FaStar,
  FaBookOpen,
  FaLaptop,
  FaCertificate,
  FaHospital
} from 'react-icons/fa';

const Section = styled.section`
  padding: 7rem 5% 5rem 5%;
  background: #ffffff;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.5rem 5% 3rem 5%;
    margin-top: 20px;
  }

  @media (max-width: 768px) {
    padding: 3rem 5% 3rem 5%;
  }
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  position: relative;
  font-family: 'Times New Roman', serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-bottom: 2rem;
  }

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Title = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: 2.2rem;
  color: #1a1a2e;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 2px;
    max-width: 120px;
    background: linear-gradient(90deg, #3730A3, #a78bfa);
    opacity: .6;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.6rem;
    gap: 10px;

    &::before,
    &::after {
      max-width: 80px;
    }
  }

  @media(max-width:768px){
    font-size: 1.3rem;
    gap: 8px;
  }

  @media(max-width:480px){
    font-size: 1.1rem;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 1.2rem;
  }

  @media(max-width:768px){
    display: flex;
    overflow-x: auto;
    scroll-behavior: smooth;
    gap: 1rem;
    padding: 0 35px;
  }

  scrollbar-width: none;

  &::-webkit-scrollbar{
    display: none;
  }
`;

const OptionCard = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 15px rgba(55, 48, 163, 0.04);
  border: 1px solid rgba(55, 48, 163, 0.08);
  text-align: left;
  transition: 0.2s;
  font-family: 'Times New Roman', serif;

  &:hover{
    box-shadow: 0 10px 25px rgba(55, 48, 163, 0.08);
    border-color: rgba(55, 48, 163, 0.2);
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    border-radius: 12px;
  }

  @media(max-width:768px){
    min-width: 260px;
    border-radius: 12px;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  margin: 0;

  img{
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: top center;
    background: #f5f3ff;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    height: 150px;

    img {
      object-fit: contain;
      object-position: top center;
    }
  }

  @media(max-width:768px){
    height: 140px;

    img {
      object-fit: contain;
      object-position: top center;
    }
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1rem;
  }
`;

const OptionTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  font-size: 1.3rem;
  color: #1a1a2e;
  margin-bottom: 0.8rem;
  font-weight: 800;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  @media(max-width:768px){
    font-size: 1rem;
  }
`;

const OptionDescription = styled.p`
  font-family: 'Times New Roman', serif;
  color: #4a4a6a;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.78rem;
    line-height: 1.4;
    margin-bottom: 0.6rem;
  }

  @media(max-width:768px){
    font-size: 0.8rem;
  }
`;

const StatsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 0.3rem;
    margin-bottom: 0.6rem;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: 'Times New Roman', serif;
  font-size: 0.85rem;
  color: #4a4a6a;

  svg{
    color: #3730A3;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.72rem;
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(90deg, transparent, #3730A3, transparent);
  opacity: 0.2;
  margin: 1rem 0;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin: 0.6rem 0;
  }
`;

const Badge = styled.span`
  font-family: 'Times New Roman', serif;
  background: rgba(55, 48, 163, 0.06);
  color: #3730A3;
  padding: 0.2rem 0.9rem;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
  display: inline-block;
  letter-spacing: 1px;
  border: 1px solid rgba(55, 48, 163, 0.1);

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.62rem;
    padding: 0.15rem 0.7rem;
    margin-bottom: 0.4rem;
  }
`;

const ActionLink = styled(Link)`
  font-family: 'Times New Roman', serif;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #3730A3;
  font-size: 0.9rem;
  text-decoration: none;
  font-weight: 700;

  &:hover{
    color: #0b0b77;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.75rem;
  }
`;

const SlideButton = styled.button`
  position: absolute;
  top: 55%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: #3730A3;
  color: #fff;
  cursor: pointer;
  z-index: 5;
  display: none;
  font-family: 'Times New Roman', serif;

  @media(max-width:768px){
    display: block;
  }
`;

const LeftButton = styled(SlideButton)`
  left: -5px;
`;

const RightButton = styled(SlideButton)`
  right: -5px;
`;

const LearningOptions = () => {
  const sliderRef = useRef();

  const scrollLeft = () => {
    sliderRef.current.scrollBy({left:-260,behavior:'smooth'});
  };

  const scrollRight = () => {
    sliderRef.current.scrollBy({left:260,behavior:'smooth'});
  };

  return (
    <Section>
      <Container>
        <Header>
          <Title>Empowering You with Flexible Learning</Title>
        </Header>

        <LeftButton onClick={scrollLeft}>‹</LeftButton>
        <RightButton onClick={scrollRight}>›</RightButton>

        <OptionsGrid ref={sliderRef}>
          <OptionCard>
            <ImageContainer>
              <img src="/images/corporate-training.png" alt="Corporate Training"/>
            </ImageContainer>
            <CardBody>
              <Badge>ENTERPRISE</Badge>
              <OptionTitle>Corporate Training Programs</OptionTitle>
              <OptionDescription>
                Custom training solutions for hospitals and RCM companies
                to train their staff in medical coding and billing.
              </OptionDescription>
              <Divider/>
              <StatsRow>
                <StatItem>
                  <FaHospital/> 50+ healthcare partners
                </StatItem>
                <StatItem>
                  <FaUsers/> 1000+ staff trained
                </StatItem>
              </StatsRow>
              <ActionLink to="/corporate-training">
                Request a Quote <FaArrowRight/>
              </ActionLink>
            </CardBody>
          </OptionCard>

          <OptionCard>
            <ImageContainer>
              <img src="/images/individual-training.png" alt="Individual Training"/>
            </ImageContainer>
            <CardBody>
              <Badge>INDIVIDUAL</Badge>
              <OptionTitle>Career Development Programs</OptionTitle>
              <OptionDescription>
                Learn medical coding with programs covering DRG, HCC,
                CDI and certification preparation.
              </OptionDescription>
              <Divider/>
              <StatsRow>
                <StatItem>
                  <FaLaptop/> 30+ online courses
                </StatItem>
                <StatItem>
                  <FaCertificate/> CCS, CPC, CIC prep
                </StatItem>
              </StatsRow>
              <ActionLink to="/courses">
                Explore Programs <FaArrowRight/>
              </ActionLink>
            </CardBody>
          </OptionCard>

          <OptionCard>
            <ImageContainer>
              <img src="/images/free-resources.png" alt="Free Resources"/>
            </ImageContainer>
            <CardBody>
              <Badge>FREE</Badge>
              <OptionTitle>Educational Resources & Updates</OptionTitle>
              <OptionDescription>
                Get ICD-10 updates, coding scenarios, and industry
                insights through our monthly newsletter.
              </OptionDescription>
              <Divider/>
              <StatsRow>
                <StatItem>
                  <FaBookOpen/> Monthly coding updates
                </StatItem>
                <StatItem>
                  <FaStar/> 3,500+ subscribers
                </StatItem>
              </StatsRow>
              <ActionLink to="/newsletter-signup">
                Subscribe Now <FaArrowRight/>
              </ActionLink>
            </CardBody>
          </OptionCard>
        </OptionsGrid>
      </Container>
    </Section>
  );
};

export default LearningOptions;